// Worker <-> UI message protocol (spec §4.2) and the SimEvent/SimError schemas
// (§5.4, §4.4). Keep SimEvent stable — a future 2D/3D visualization consumes it.

export type SimEvent = {
  t: number; // simulated ms since run start
  dev: "servo" | "rgb" | "buzzer" | "action" | "ultrasonic" | "imu" | "battery" | "pose" | "system";
  op: string;
  args: Record<string, number | string | boolean>;
  desc: string;
};

export type SimError = {
  kind: "syntax" | "runtime" | "api" | "budget";
  line: number | null;
  column?: number | null;
  message: string;
  hint?: string;
  traceback?: string;
};

export type StdoutChunk = { t: number; text: string };

// World & sensor inputs (Phase 3).
// kind: 0 = wall (full height), 1 = crossbar (head-level only).
export type Rect = { x: number; y: number; w: number; h: number; kind?: 0 | 1 };
export type Blob = [number, number, number, number] | null;
export type RobotPose = { x: number; y: number; heading: number };
// A coloured line segment the body camera follows. color = camera colour id 1-5.
export type Line = { x1: number; y1: number; x2: number; y2: number; color: number };

// UI -> worker
export type WorkerCommand =
  | { type: "init"; sab: SharedArrayBuffer | null } // provide the LIVE-mode shared buffer
  | { type: "run"; code: string; speed: number } // speed: 1 = real-time, 0 = instant
  | { type: "sensorSet"; sensor: "ultrasonic" | "imu" | "camera" | "world"; value: unknown }
  | { type: "reset" }
  | { type: "stop" };

// worker -> UI
export type WorkerMessage =
  | { type: "ready" }
  | { type: "stdout"; text: string; t: number }
  | { type: "event"; event: SimEvent }
  | { type: "error"; error: SimError }
  | { type: "done"; elapsedSimMs: number };

// Default run-time budgets (§4.1).
export const DEFAULT_BUDGET = {
  maxSimMs: 30 * 60 * 1000, // 30 min simulated
  maxEvents: 50_000,
  wallClockMs: 20_000, // main-thread watchdog for loops that never sleep
};

// SharedArrayBuffer layout for LIVE mode (only usable when crossOriginIsolated).
// Int32 control block at byte 0 (Atomics.wait futex + stop flag); Float64 live
// sensor values at byte 8.
export const SAB = {
  bytes: 32,
  ctrlLen: 2, // Int32Array(sab, 0, 2)
  CTRL_WAIT: 0,
  CTRL_STOP: 1,
  valuesByteOffset: 8,
  valuesLen: 2, // Float64Array(sab, 8, 2)
  VAL_ULTRASONIC: 0, // NaN => world ray-cast
  VAL_IMU: 1,
};
